/**
 * Privacy Policy Page
 * CHOMP Pet Dental - Veterinary Clinic
 */

import React from 'react';
import Footer from '../components/Footer';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-brand-teal text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl opacity-90">
            Last Updated: May 22, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="lead text-xl text-gray-700 mb-8">
              CHOMP Pet Dental ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://chomp.vet" className="text-brand-teal hover:underline">chomp.vet</a> and use our veterinary services.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Personal Information</h3>
            <p className="text-gray-700 mb-4">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Book an appointment or consultation</li>
              <li>Contact us via phone, email, or contact form</li>
              <li>Create an account or client profile</li>
              <li>Subscribe to our newsletter or marketing communications</li>
            </ul>
            <p className="text-gray-700 mb-4">
              This information may include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Name, email address, phone number, and mailing address</li>
              <li>Pet information (name, species, breed, age, medical history)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Emergency contact information</li>
              <li>Veterinary history and medical records</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Automatically Collected Information</h3>
            <p className="text-gray-700 mb-4">
              When you visit our website, we automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>IP address, browser type, and operating system</li>
              <li>Pages visited, time spent on pages, and referring website</li>
              <li>Cookies and similar tracking technologies (see our Cookie Policy below)</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Provide veterinary dental services to your pet</li>
              <li>Schedule appointments and send appointment reminders</li>
              <li>Process payments and billing</li>
              <li>Communicate with you about your pet's care, including test results and treatment plans</li>
              <li>Send SMS text messages related to customer care, such as appointment reminders and follow-ups (only with your consent)</li>
              <li>Send educational materials and newsletters (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations and maintain medical records</li>
              <li>Prevent fraud and ensure security</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">
              We do not sell, trade, or rent your personal information to third parties. Mobile opt-in, SMS consent, and phone numbers collected for SMS communication purposes will not be shared with any third party or affiliates for marketing purposes. We do not share or sell your personal information, including mobile phone numbers or other contact information, with third parties or affiliates for marketing purposes or lead generation. We only share information as described in this policy (e.g., with service providers under contract, for legal requirements, or with your consent for pet care coordination). We may share your information in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>With Your Consent:</strong> We may share information with your referring veterinarian or other healthcare providers involved in your pet's care</li>
              <li><strong>Service Providers:</strong> We use trusted third-party service providers (payment processors, email services, analytics) who are contractually obligated to protect your information</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity</li>
              <li><strong>Emergency Situations:</strong> To protect the health and safety of your pet or others</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">4. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure servers and databases with restricted access</li>
              <li>Regular security audits and updates</li>
              <li>Staff training on privacy and data protection</li>
            </ul>
            <p className="text-gray-700 mb-4">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">5. Medical Records Retention</h2>
            <p className="text-gray-700 mb-4">
              In accordance with Florida veterinary regulations, we maintain medical records for a minimum of 4 years from the date of last service. Records may be retained longer if required by law or for legitimate business purposes.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">6. Your Rights and Choices</h2>
            <p className="text-gray-700 mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information and your pet's medical records</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your information (subject to legal record retention requirements)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time by clicking the unsubscribe link</li>
              <li><strong>Restrict Processing:</strong> Request that we limit how we use your information</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise these rights, please contact us at <a href="mailto:info@chomp.vet" className="text-brand-teal hover:underline">info@chomp.vet</a> or call <a href="tel:+19544663775" className="text-brand-teal hover:underline">(954) 466-3775</a>.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Essential Cookies:</strong> Necessary for website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site (Google Analytics)</li>
              <li><strong>Marketing Cookies:</strong> Used for advertising and retargeting</li>
            </ul>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings. Note that disabling cookies may limit website functionality.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">8. Third-Party Links</h2>
            <p className="text-gray-700 mb-4">
              Our website may contain links to third-party websites (e.g., social media, payment processors). We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Posting the updated policy on this page with a new "Last Updated" date</li>
              <li>Sending an email notification (for material changes)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Your continued use of our services after changes are posted constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <p className="font-semibold text-primary mb-2">CHOMP Pet Dental</p>
              <p className="text-gray-700">1800 E Commercial Blvd</p>
              <p className="text-gray-700">Fort Lauderdale, FL 33308</p>
              <p className="text-gray-700 mt-3">
                <strong>Phone:</strong> <a href="tel:+19544663775" className="text-brand-teal hover:underline">(954) 466-3775</a>
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:info@chomp.vet" className="text-brand-teal hover:underline">info@chomp.vet</a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong> <a href="https://chomp.vet" className="text-brand-teal hover:underline">chomp.vet</a>
              </p>
            </div>

            <div className="bg-brand-teal/10 border-l-4 border-brand-teal rounded-lg p-6 mt-8">
              <p className="text-gray-800 font-medium">
                <strong>Florida Residents:</strong> If you are a Florida resident, you may have additional rights under Florida law. Please contact us for more information.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
